// Dashboard data: one GitHub search per section query, each its own request
// (run in parallel, rendered as it arrives, failing alone), then the
// attention inputs for the rows shown, in batched `nodes(ids:)` requests.
//
// The store composes the dashboard's modules (state/dashboard/):
// - search.ts: the sections' searches and rows read by ID;
// - attention.ts: working out the rows' attention sets;
// - stacks.ts: the pool stacks are found among, and the native stack
//   members no section loaded;
// - snapshot.ts (with dashCache.ts): the saved copy shown on opening, and
//   saved after each load;
// - program.ts: all of it as a runtime Program, whose state this class
//   shows to Svelte (`toState`) and whose messages its methods send.
// The browser extension's service worker runs the same store.
import { rateLimitedUntil } from '../api/gh';
import { onIdentityChange } from '../api/identity';
import type { Notice, NoticeHandler } from '../api/sse';
import type { AttentionInput, AttentionResult } from '../engine/types';
import type { Section } from '../logic/sections';
import { dashboardCold, dashboardSteps, type LoadView } from '../loading/steps';
import { Program, ProgramDisposed } from '../runtime/effects';
import { manual, type Remote, type Retry } from '../runtime/remote';
import type { Failure } from '../api/failure';
import { toState } from '../runtime/state.svelte';
import { sharedAttentionInputs, type AttentionInputs } from './attentionInputs';
import type { SnapshotStore } from './dashCache';
import { CheckQueue } from './dashboard/attention';
import { busy, checkingOf, initialState, makeUpdate, refreshingOf, type DashState, type Msg, type SectionRows } from './dashboard/program';
import type { DashRow } from './dashboard/rows';
import type { RowRead, SearchOptions, SearchPage } from './dashboard/search';

export { CHECK_RETRY_MS, OPENING_WAIT_MS, RECHECK_DELAY_MS, type SectionLoad, type SectionRows, type SectionUnloaded } from './dashboard/program';
export { readRows, searchSection, type RowRead, type SearchOptions, type SearchPage, type SectionCut } from './dashboard/search';
export { OUTSIDE_MAX } from './dashboard/stacks';
export type { DashRow } from './dashboard/rows';

/** How current the dashboard on screen is (DashboardStore.freshness). */
export interface Freshness {
  /** When the oldest rows on screen were read from GitHub (epoch ms); null while none are shown. */
  at: number | null;
  /** Whether GitHub is being asked for newer ones (a load, a background check or a Refresh). */
  checking: boolean;
  /** Why the refresh of a section failed, its earlier rows kept on screen; null when none did. */
  failure: Failure | null;
  /** Whether that refresh is tried again by itself (`Scheduled`) or only when the user asks. */
  retry: Retry;
}

/** A live notice subscription: closed when the topics change or the store is disposed. */
export interface LiveSubscription {
  close(): void;
}

/**
 * What a dashboard store works with, given by whoever makes it, so the
 * store itself reaches for no window, document or worker. The app passes
 * `searchSection`, the engine worker's `eng.attention` and `LiveEvents`; the
 * browser extension's service worker passes the engine run in place and no
 * live source.
 */
export interface DashboardCapabilities {
  /** The viewer's login. */
  me: () => string;
  /**
   * One section query: the first `first` hits, how many match, and how
   * many could not load (`searchSection`). It sends its requests of a long
   * search abortable by `opts.signal` and hands rows over through
   * `opts.onRows` as they load; a search that ignores both is still
   * correct, only slower.
   */
  search: (q: string, first: number, opts: SearchOptions) => Promise<SearchPage>;
  /** A row's attention set, by the engine's rules. */
  attention: (input: AttentionInput) => Promise<AttentionResult>;
  /** Subscribes to live notices on `topics`; without it, the store never listens. */
  live?: (topics: string[], handler: NoticeHandler) => LiveSubscription;
  /** Attention inputs kept between loads; the page's shared ones by default. */
  inputs?: AttentionInputs;
  /**
   * Where the dashboard as last read is saved, to show at once the next
   * time it opens (`open`); without it, nothing is saved.
   */
  snapshot?: SnapshotStore;
  /**
   * Rows by node ID, at most DASH_PAGE, null for an ID GitHub does not show
   * the viewer and `sso` for one whose read needs SSO authorization
   * (`readRows`): the native stack members no section loaded. Without it,
   * those show as numbers alone.
   */
  rows?: (ids: string[]) => Promise<RowRead[]>;
  /** Until when GitHub calls are paused (epoch ms), or null: the app's scheduler's by default. */
  pausedUntil?: () => number | null;
  /** What resets the store (another account signing in): `onIdentityChange` by default. */
  resetOn?: (listener: () => void) => () => void;
}

/** Repository topics the dashboard listens on, at most (one SSE subscription each). */
const MAX_REPO_TOPICS = 40;

export class DashboardStore {
  private readonly program: Program<DashState, Msg>;
  private readonly view: { readonly current: DashState };
  private live: LiveSubscription | null = null;
  private topicsKey = '';
  private userId: number | null = null;
  private readonly unwatch: () => void;
  private disposed = false;

  constructor(private caps: DashboardCapabilities) {
    const kept = caps.inputs ?? sharedAttentionInputs;
    const update = makeUpdate({
      me: caps.me,
      search: caps.search,
      rows: caps.rows ?? null,
      snapshot: caps.snapshot ?? null,
      kept,
      attention: { kept, engine: caps.attention, checks: new CheckQueue() },
      send: (m) => this.program.send(m),
      pausedUntil: caps.pausedUntil ?? rateLimitedUntil,
      now: Date.now,
      random: Math.random,
    });
    this.program = new Program<DashState, Msg>({ init: () => [initialState()], update, resetOn: caps.resetOn ?? onIdentityChange });
    this.view = toState(this.program);
    // The rows shown name the topics listened on.
    const unwatch = this.program.subscribe(() => this.resubscribe());
    // A pull request that changed (the app wrote to it in any tab, the viewer opened it, a notice named it) is read again.
    const unhear = kept.onChanged((key) => this.program.send({ t: 'changed', key }));
    this.unwatch = () => {
      unwatch();
      unhear();
    };
  }

  private get s(): DashState {
    return this.view.current;
  }

  /** The sections shown. */
  get sections(): Section[] {
    return this.s.sections;
  }

  /** Each section's load: loading, failed, or its rows with their freshness and gaps. */
  get state(): Remote<SectionRows>[] {
    return sectionLoads(this.s);
  }

  /**
   * Every row loaded by any section's latest successful search, once each
   * (its most recently updated copy), with its attention as known: the rows
   * an attention section leaves out too. Stacks are found among them. The
   * native stack members no section loaded, read by ID, are here too,
   * marked `outside`, their attention unknown.
   */
  get pool(): DashRow[] {
    return this.s.pool;
  }

  /** Native stack members GitHub does not show the viewer, by `memberKey`: read by ID, they came back as nothing. */
  get hidden(): ReadonlySet<string> {
    return this.s.outside.hidden;
  }

  /** Native stack members whose read by ID needs SSO authorization, by `memberKey`: not saved, and read again on every load. */
  get sso(): ReadonlySet<string> {
    return this.s.outside.sso;
  }

  /** Whether `open` is still reading the saved copy (at most OPENING_WAIT_MS): the view shows no loading placeholders meanwhile. */
  get opening(): boolean {
    return this.s.opening;
  }

  /** Whether nothing of any section is there to show yet (still opening, or every search under way): the view's loading screen is due. */
  get cold(): boolean {
    return this.s.opening || dashboardCold(this.s);
  }

  /** The steps of the dashboard's load (loading/steps.ts): what its loading screen shows. */
  get loadView(): LoadView {
    return dashboardSteps(this.s);
  }

  /** Whether a Refresh from GitHub is running (`refresh`). */
  get refreshing(): boolean {
    return refreshingOf(this.s);
  }

  /**
   * How current the rows on screen are: when they were read, whether newer
   * ones are being read, and why the latest read failed where it did.
   */
  get freshness(): Freshness {
    const s = this.s;
    let at: number | null = null;
    let failure: Failure | null = null;
    let retry: Retry = manual;
    for (const slot of s.slots) {
      const r = slot.shown;
      if (r.tag !== 'Has') continue;
      const t = slot.readAt;
      if (t !== undefined && (at === null || t < at)) at = t;
      const f = r.freshness;
      if (!failure && f.kind === 'stale' && f.refresh.kind === 'failed') ({ reason: failure, retry } = f.refresh);
    }
    return { at, checking: checkingOf(s), failure, retry };
  }

  /** When section `si`'s rows on screen were read from GitHub (epoch ms), or null when none are shown. */
  readAtOf(si: number): number | null {
    const slot = this.s.slots[si];
    return slot?.shown.tag === 'Has' ? (slot.readAt ?? null) : null;
  }

  /**
   * Open the dashboard on these sections: show at once those the saved
   * copy holds (`caps.snapshot`), rows, attention and all, then ask GitHub
   * for newer ones in the background (`refetch`) when the copy is older
   * than SNAPSHOT_STALE_MS, lacks a section, has a section some of whose
   * matches did not load, or `revalidate` says to. A row whose attention
   * the copy does not hold, or that changed since its section was read (the
   * invalidation log, `AttentionInputs.changedSince`), is shown without it
   * and worked out again; an attention section holds such rows back,
   * counting them in `checking`. Without a usable copy, it is a `load`.
   * Resolves once that work is done.
   */
  open(sections: Section[], opts: { revalidate?: boolean } = {}): Promise<void> {
    return this.until({ t: 'open', sections, revalidate: opts.revalidate ?? false }, (s) => !busy(s));
  }

  /**
   * Load these sections afresh, each showing as loading until its search
   * answers. A later `load` or `refetch` supersedes one still running.
   * `fresh` (Try again) reads every row's attention inputs from GitHub,
   * using none kept from earlier loads.
   */
  load(sections: Section[], opts: { fresh?: boolean } = {}): Promise<void> {
    return this.until({ t: 'load', sections, fresh: opts.fresh ?? false }, (s) => !busy(s));
  }

  /**
   * Load the current sections again, keeping each ready section's rows on
   * screen until its new results arrive. A later `load` or `refetch`
   * supersedes one still running. While a Refresh runs (`refreshing`), it
   * waits for it and runs after, once however often it was asked.
   */
  refetch(): Promise<void> {
    if (refreshingOf(this.s)) return this.until({ t: 'refetch' }, (s) => !refreshingOf(s));
    return this.until({ t: 'refetch' }, (s) => !busy(s));
  }

  /**
   * Load the current sections again from GitHub alone (Refresh from
   * GitHub): every row's attention inputs are read afresh, none kept from
   * earlier loads or saved, while the rows on screen stay until their new
   * ones arrive. Until a run completes, every run started (a section edit,
   * say) reads afresh too, and refetches (notices, focus, the end of a
   * rate-limit pause) wait for it. Resolves when it is done.
   */
  refresh(): Promise<void> {
    // Done once its run is, the stack members that run named are read, and it is saved.
    return this.until({ t: 'refresh' }, (s) => !refreshingOf(s) && s.tail === null);
  }

  /**
   * Load section `si` again, alone (its Try again), keeping a ready
   * section's rows on screen meanwhile. A full `load` or `refetch` started
   * meanwhile supersedes it. Its rows join the subscriptions. Attention
   * that could not be told is worked out again from fresh inputs; known
   * attention is kept.
   */
  reloadSection(si: number): Promise<void> {
    return this.until({ t: 'reload', si }, (s) => !busy(s));
  }

  /**
   * Resolves once the store has no work under way, as `load` and the others
   * do when theirs ends (at once when it has none, or is disposed): for work
   * no call of the caller's started, such as what a notice led to.
   */
  settled(): Promise<void> {
    return this.disposed ? Promise.resolve() : this.wait((s) => !busy(s));
  }

  /**
   * Resolves once `shows(this)` holds (at once if it does now), checked as
   * the store's state changes; at once when the store is disposed. For a
   * caller waiting for what the store shows, such as a section's rows.
   */
  when(shows: (store: this) => boolean): Promise<void> {
    return this.disposed ? Promise.resolve() : this.wait(() => shows(this));
  }

  /** Send `m`, and resolve once `done` holds (at once when the store is disposed). */
  private async until(m: Msg, done: (s: DashState) => boolean): Promise<void> {
    if (this.disposed) return;
    this.program.send(m);
    await this.wait(done);
  }

  /** Resolve once `done` holds (at once when the store is disposed). */
  private async wait(done: (s: DashState) => boolean): Promise<void> {
    try {
      await this.program.until(done);
    } catch (e) {
      if (!(e instanceof ProgramDisposed)) throw e;
    }
  }

  /**
   * Listen for changes to the pull requests shown: their repositories'
   * topics (pull request, review, thread and comment notices) plus the user
   * topic for reconnects. Review-mark notices are ignored: marks change no row.
   */
  subscribe(userId: number | null) {
    if (userId === null || !this.caps.live) return;
    this.userId = userId;
    this.resubscribe();
  }

  /** Topics for the current rows (the user topic, then repositories by name). */
  topics(): string[] {
    if (this.userId === null) return [];
    const repos = [...this.program.state.shown.keys()].sort().slice(0, MAX_REPO_TOPICS);
    return [`user:${this.userId}`, ...repos.map((r) => `repo:${r}`)];
  }

  private resubscribe() {
    const live = this.caps.live;
    if (this.userId === null || this.disposed || !live) return;
    const topics = this.topics();
    const key = topics.join(',');
    if (key === this.topicsKey && this.live) return;
    this.topicsKey = key;
    this.live?.close();
    this.live = live(topics, (topic, notice) => this.onNotice(topic, notice));
  }

  /** Handle a notice, or a trigger (open/reconnect/resync/focus) when `notice` is null. */
  onNotice(topic: string, notice: Notice | null) {
    if (this.disposed) return;
    this.program.send({ t: 'notice', topic, notice });
  }

  /** Stop: everything in flight is aborted and dropped, nothing more is listened to. */
  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.unwatch();
    this.program.dispose();
    this.live?.close();
    this.live = null;
  }
}

const loads = new WeakMap<DashState, Remote<SectionRows>[]>();

/** Each section's load, one array per state (so a view reading it twice sees the same one). */
function sectionLoads(s: DashState): Remote<SectionRows>[] {
  let out = loads.get(s);
  if (!out) {
    out = s.slots.map((x) => x.shown);
    loads.set(s, out);
  }
  return out;
}
